import type { Selector } from 'reselect';
import { createSelector } from 'reselect';

import type {
  IIndexedSeedProducts,
  IIndexedSeedSubgroups,
  IIndexedTreatments,
  ISeedProduct,
  ISeedProductSubgroup,
  ISeedProductsState,
  ITreatment,
} from './types';

export const selectProducts = (state: ISeedProductsState) => state.products;
export const selectSubgroups = (state: ISeedProductsState) => state.subGroups.data;

export const selectIndexedProducts: Selector<ISeedProductsState, IIndexedSeedProducts> =
  createSelector(selectProducts, (products) =>
    products.reduce((indexed: { [x: string]: any }, product: ISeedProduct) => {
      if (product.itemReference) {
        indexed[product.itemReference] = product;
      }
      return indexed;
    }, {}),
  );

export const selectProduct: Selector<ISeedProductsState, ISeedProduct> = createSelector(
  [
    selectIndexedProducts,
    (_: ISeedProductsState, packagedItemReference: string) => packagedItemReference,
  ],
  (indexedProducts, packagedItemReference) => indexedProducts[packagedItemReference],
);

export const selectIndexedSubgroups: Selector<ISeedProductsState, IIndexedSeedSubgroups> =
  createSelector(selectSubgroups, (subgroups) =>
    subgroups.reduce((indexed: { [x: string]: any }, subgroup: ISeedProductSubgroup) => {
      if (subgroup.subGroupReference) {
        indexed[subgroup.subGroupReference] = subgroup;
      }
      return indexed;
    }, {}),
  );

export const selectSubgroup: Selector<ISeedProductsState, ISeedProductSubgroup> = createSelector(
  [selectIndexedSubgroups, (_: ISeedProductsState, subGroupReference: string) => subGroupReference],
  (indexedSubgroups, subGroupReference) => indexedSubgroups[subGroupReference],
);

export const selectIndexedTreatments: Selector<ISeedProductsState, IIndexedTreatments> =
  createSelector(selectSubgroups, (subgroups) =>
    subgroups.reduce((indexed: { [x: string]: any }, subgroup: ISeedProductSubgroup) => {
      if (subgroup.treatments && Array.isArray(subgroup.treatments)) {
        for (const treatment of subgroup.treatments) {
          indexed[treatment.treatmentReference] = treatment;
        }
      }
      return indexed;
    }, {}),
  );

export const selectTreatment: Selector<ISeedProductsState, ITreatment> = createSelector(
  [
    selectIndexedTreatments,
    (_: ISeedProductsState, treatmentReference: string) => treatmentReference,
  ],
  (indexedTreatments, treatmentReference) => indexedTreatments[treatmentReference],
);
